import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield,
  Lock,
  Eye,
  Server,
  FileCheck,
  Globe,
  Heart,
  Sparkles,
  BookOpen,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const CORE_VALUES = [
  {
    icon: Heart,
    title: 'Empathy at Scale',
    desc: 'We translate silent struggles into clear, actionable workplace adjustments.',
  },
  {
    icon: Lock,
    title: 'Uncompromising Privacy',
    desc: 'We believe trust is non-negotiable. Individual identities always remain mathematically invisible.',
  },
  {
    icon: BookOpen,
    title: 'Scientific Authority',
    desc: 'No guessing games. Our pulse surveys are engineered based on leading peer-reviewed organizational research.',
  },
];

const PRIVACY_PILLARS = [
  {
    icon: Lock,
    title: 'End-to-End Encryption',
    desc: 'Every survey response is encrypted from the moment it leaves the employee\'s device. At no point does any human — including Udvega staff — access raw individual data.',
  },
  {
    icon: Eye,
    title: 'Identity Stripping',
    desc: 'Before any data enters our predictive engine, all personally identifiable information is permanently removed from the analysis pipeline.',
  },
  {
    icon: Server,
    title: 'Anonymity Firewall',
    desc: 'Results only display when a team has 2+ respondents, making it mathematically impractical to trace any score back to a specific individual.',
  },
  {
    icon: Shield,
    title: 'Zero-Knowledge Architecture',
    desc: 'Our system is designed so that even our own engineers cannot reverse-engineer individual responses. Aggregation happens before any query is executed.',
  },
  {
    icon: FileCheck,
    title: 'Compliance Standards',
    desc: 'Udvega is designed to meet ISO 27001, GDPR, and SOC 2 Type II standards, with regular third-party security audits.',
  },
  {
    icon: Globe,
    title: 'Global Data Residency',
    desc: 'Data is stored in the region of your choice, supporting multi-region deployment for data sovereignty and minimal latency.',
  },
];

export default function About() {
  return (
    <div className="min-h-screen pt-20 bg-[#F8FAFC]" data-testid="about-page">
      {/* Our Story Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-teal-950">
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at 30% 50%, #0D9488 0%, transparent 55%)' }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-600/20 border border-teal-500/30 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-teal-300" />
              <span className="text-xs font-medium text-teal-300 tracking-wide">Workplace Mental Health · Data-driven HR</span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight font-semibold text-white leading-tight mb-6">
              Our Mission: Humanizing the Modern Workplace.
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-slate-400 leading-relaxed">
              Udvega was born out of a simple observation: in the race for productivity, companies were losing their most valuable asset—their people. We set out to build a platform that acts as an early-warning system for burnout, giving HR leaders the power of predictive data while strictly guarding the psychological safety of every employee.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Privacy Promise */}
      <section className="py-16 md:py-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-3xl">
            <motion.p variants={fadeUp} className="text-xs font-semibold tracking-[0.1em] uppercase text-teal-600 mb-3">The Privacy Promise</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-5">
              Employee-First Culture Starts with Trust
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-slate-600 leading-relaxed">
              At Udvega, protecting employee well-being should never come at the cost of privacy. Our platform is built on one uncompromising principle:{' '}
              <strong className="text-slate-800">individual identities must remain invisible.</strong> That is how we enable honest feedback, data-driven HR decisions, and a truly employee-first culture.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Security / Privacy Pillars */}
      <section className="py-20 md:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {PRIVACY_PILLARS.map((item) => (
              <motion.div
                key={item.title}
                variants={fadeUp}
                className="bg-white rounded-2xl p-8 shadow-xl shadow-slate-300/25 hover:shadow-2xl hover:shadow-teal-500/10 hover:-translate-y-1.5 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-2xl gradient-teal flex items-center justify-center mb-5">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading text-lg font-medium text-slate-800 mb-3">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-semibold tracking-[0.1em] uppercase text-teal-600 mb-3 text-center">
              What We Stand For
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl md:text-4xl font-semibold text-slate-800 mb-12 text-center">
              Our Core Values
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {CORE_VALUES.map((v) => (
                <motion.div
                  key={v.title}
                  variants={fadeUp}
                  className="text-center p-8 rounded-3xl bg-gradient-to-b from-slate-50 to-white border border-slate-100 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-teal-500/10 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-2xl gradient-teal flex items-center justify-center mx-auto mb-5">
                    <v.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-800 mb-3">{v.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Science — human-centric reframe */}
      <section className="py-20 md:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-semibold tracking-[0.1em] uppercase text-teal-600 mb-3">
              Predictive Burnout Science
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl md:text-4xl font-semibold text-slate-800 mb-6">
              Rigorous Science. Human-Centric AI.
            </motion.h2>
            <motion.div variants={fadeUp} className="max-w-3xl space-y-8">
              <p className="text-base text-slate-600 leading-relaxed">
                Our engine integrates clinically-validated psychological constructs with proprietary predictive algorithms. By analyzing aggregated patterns across workplace fatigue and team friction, Udvega achieves a{' '}
                <strong className="text-teal-700">95%+ accuracy rate</strong> in identifying stress trends before they escalate into resignations.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Team Friction', desc: 'Measures workplace culture stress and interpersonal friction signals.' },
                  { label: 'Workplace Fatigue', desc: 'Tracks emotional exhaustion and burnout risk across teams.' },
                  { label: 'Psychological Safety', desc: 'Assesses adaptability, change-readiness, and team resilience.' },
                ].map((item) => (
                  <div key={item.label} className="p-5 rounded-2xl bg-white shadow-md shadow-slate-200/60 border border-slate-100">
                    <h4 className="font-heading font-semibold text-slate-800 mb-2 text-sm">{item.label}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              <p className="text-sm text-slate-500 leading-relaxed">
                Validated across 300+ teams in 50+ countries, Udvega gives HR leaders predictive burnout science they can act on — without ever compromising employee trust.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 md:py-24 relative overflow-hidden" data-testid="about-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-teal-700 to-slate-900" />
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at 80% 50%, #2DD4BF 0%, transparent 50%)' }} />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4 tracking-tight">
              Ready to build a resilient, employee-first workplace culture?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-teal-100 mb-8 max-w-xl mx-auto">
              Join forward-thinking HR teams using data-driven insights to protect workplace mental health.
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-white text-teal-700 hover:bg-teal-50 px-10 h-14 text-base font-semibold shadow-xl shadow-teal-900/30 hover:shadow-2xl transition-shadow"
                  data-testid="about-cta-btn"
                >
                  Book a Demo Today <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
